import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { type ProductPreTestNoteFormValues, productPreTestNoteFormSchema } from "../../schemas"
import type { ProductQualityInstructions } from "../../types"

import { ProductPreTestNoteForm } from "./ProductPreTestNoteForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"

interface ProductPreTestNoteFormDialogProps {
  isOpen: boolean
  onClose: () => void
  instructions?: ProductQualityInstructions
  onSubmit: (input: ProductQualityInstructions) => Promise<void>
  isUpdating: boolean
}

export const ProductPreTestNoteFormDialog: FC<ProductPreTestNoteFormDialogProps> = ({
  isOpen,
  onClose,
  instructions,
  onSubmit,
  isUpdating,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const methods = useForm<ProductPreTestNoteFormValues>({
    defaultValues: {
      printExpirationToAkrochemCoa: instructions?.printExpirationToAkrochemCoa ?? false,
      printMfgDatesToAkrochemCoa: instructions?.printMfgDatesToAkrochemCoa ?? false,
      printPreTestNotesToAkrochemCoa: instructions?.printPreTestNotesToAkrochemCoa ?? false,
      preTestNotes: instructions?.preTestNotes ?? "",
    },
    resolver: zodResolver(productPreTestNoteFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
    reset,
  } = methods

  const canSubmit = isDirty && isValid && !isSubmitting

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
      reset()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const handleConfirmClose = () => {
    setIsCancelDialogOpen(false)
    onClose()
    reset()
  }

  const submitForm = async (formValues: ProductPreTestNoteFormValues) => {
    await onSubmit(formValues)
    reset(formValues)
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleConfirmClose} />
  ) : (
    <Dialog open={isOpen} fullWidth onClose={handleClose} data-testid='pre-test-note-form-dialog'>
      <DialogHeader title='Edit Pre Test Note' onClose={handleClose} />
      <DialogContent className='p-10'>
        <FormProvider {...methods}>
          <ProductPreTestNoteForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          data-testid='submit-button'
          variant='primary'
          disabled={!canSubmit || isUpdating}
          onClick={handleSubmit(submitForm)}
        >
          {isUpdating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
